/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { WorkspaceType } from "../services/workspaceService";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { useTour } from "@/features/Tour";
import type { Workspace } from "@/features/Workspaces/services/workspaceService";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Button } from "@/shared/components/ui/button";
import { WorkspaceForm } from "./WorkspaceForm";
import { WorkspaceUsersForm } from "./WorkspaceUsersForm";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { makeApiError } from "@/shared/utils/fetchApi";
import { useCreateProduct, useUpdateProduct } from "@/shared/hooks/api/useWorkspace";
import { useWorkspaceUserIds, useAssignWorkspaceUsers, useUpdateWorkspaceUsers } from "@/features/Workspaces/hooks/useWorkspaceUsers";
import { useOrganization } from "@/shared/hooks/api/useOrganization";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { analyticsService } from "@/shared/services/analytics";

interface ProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId?: string;
  onWorkspaceSaved?: (workspaceId: string) => void;
}

interface ProductFormData {
  name: string;
  description?: string;
  type?: WorkspaceType | null;
  logo_url?: string;
  logoFile?: File;
}

export const WorkspaceDialog = ({ open, onOpenChange, workspaceId, onWorkspaceSaved }: ProductDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { reloadUserProducts, setCurrentWorkspace } = useWorkspace();
  const tour = useTour();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState<ProductFormData | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [savedWorkspaceId, setSavedProductId] = useState<string | undefined>(workspaceId);
  // Workspace recém-criado, guardado p/ selecionar no contexto antes do redirect
  // (evita depender do cache de produtos, que pode ainda não conter o novo item).
  const [createdWorkspace, setCreatedWorkspace] = useState<Workspace | null>(null);
  const [hasLoadedInitialUsers, setHasLoadedInitialUsers] = useState(false);
  const { data: userOrganization } = useOrganization();
  const { selectedOrganization } = useSuperAdminOrg();
  const effectiveOrgId = selectedOrganization?.id ?? userOrganization?.organization_id;

  // Fetch existing workspace users when editing
  const {
    data: existingUsers = [],
    isLoading: isLoadingProductUsers,
    isFetching: isFetchingProductUsers,
  } = useWorkspaceUserIds(workspaceId);

  const isEditingProduct = !!workspaceId;
  const isUsersLoading = isEditingProduct && (isLoadingProductUsers || isFetchingProductUsers || !hasLoadedInitialUsers);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setActiveTab("general");
      setFormData(null);
      setSavedProductId(undefined);
      setCreatedWorkspace(null);
      setSelectedUsers([]); // CORREÇÃO: Limpar selectedUsers ao fechar
      setHasLoadedInitialUsers(false);
    } else if (open && workspaceId) {
      setSavedProductId(workspaceId);
      setSelectedUsers([]); // CORREÇÃO: Resetar antes de carregar usuários existentes
       setHasLoadedInitialUsers(false);
      // Forçar refetch dos usuários do produto
      queryClient.invalidateQueries({ queryKey: ['workspace-user-ids', workspaceId] });
    } else if (open && !workspaceId) {
      // Novo produto: não pré-selecionar usuários (backend vincula automaticamente o criador)
      setSelectedUsers([]);
      setSavedProductId(undefined);
      setHasLoadedInitialUsers(true);
    }
  }, [open, workspaceId, queryClient]);

  // Set initial selected users when editing
  useEffect(() => {
    if (open && workspaceId && existingUsers !== undefined) {
      // Carregar usuários existentes mesmo se a lista estiver vazia
      // Isso garante que produtos sem usuários também sejam tratados corretamente
      setSelectedUsers(existingUsers);
      setHasLoadedInitialUsers(true);
    }
  }, [open, workspaceId, existingUsers]);

  // Memoizar selectedUsers para evitar re-renders desnecessários
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedSelectedUsers = useMemo(() => selectedUsers, [selectedUsers.join(',')]);

  const handleSuccess = async (shouldClose: boolean = true) => {
    const newWorkspaceId = savedWorkspaceId;
    const isNewProduct = !workspaceId;
    
    // Invalidar queries
    queryClient.invalidateQueries({ queryKey: ['current-organization-products'] });
    queryClient.invalidateQueries({ queryKey: ['user-products'] });
    if (newWorkspaceId) {
      queryClient.invalidateQueries({ queryKey: ['workspace-user-ids', newWorkspaceId] });
    }
    
    // Recarregar produtos para atualizar a navbar
    await reloadUserProducts();
    
    // Track workspace creation/update
    if (isNewProduct && formData) {
      const productsData = queryClient.getQueryData<any[]>(['user-products', user?.id]);
      const isFirstProduct = !productsData || productsData.length === 0 || (productsData.length === 1 && productsData[0].id === newWorkspaceId);
      
      if (isFirstProduct) {
        analyticsService.trackFirstProductCreated({
          product_name: formData.name,
        });
      }
      
      analyticsService.trackEvent('product_created', {
        product_name: formData.name,
        has_description: !!formData.description,
        is_first_product: isFirstProduct
      });
    } else if (!isNewProduct && formData) {
      analyticsService.trackEvent('product_updated', {
        product_name: formData.name,
        has_description: !!formData.description
      });
    }
    
    // Produto novo: selecionar imediatamente o workspace criado para que o
    // view-scope vire 'product' antes do redirect — senão o WorkspaceGuard,
    // ainda em scope 'organization', joga de volta pra "/". Usa o objeto
    // retornado pela criação (não o cache, que pode estar defasado).
    if (isNewProduct && createdWorkspace) {
      setCurrentWorkspace(createdWorkspace);
    }
    
    // Só fechar o dialog e notificar se for finalização completa
    if (shouldClose) {
      // Notificar o componente pai sobre o produto salvo
      if (onWorkspaceSaved && newWorkspaceId) {
        onWorkspaceSaved(newWorkspaceId);
      }
      
      // Fechar o dialog
      onOpenChange(false);

      // Resetar estado
      setActiveTab("general");
      setFormData(null);
      setSelectedUsers([]); // CORREÇÃO: Resetar selectedUsers ao finalizar
      setSavedProductId(undefined);

      // Produto novo: redirecionar para a página do workspace recém-criado
      // (mesmo destino do switch de workspace na Navbar).
      // EXCETO durante o Tour Guiado: o tour dirige a navegação para o próximo
      // passo (ex.: /integrations) e este redirect sobrescreveria, deixando o
      // usuário preso na tela de config em vez de avançar. (Regressão #83778)
      if (isNewProduct && newWorkspaceId && !tour?.tourState.isActive) {
        navigate(`/products/${newWorkspaceId}/config`);
      }
    }
  };

  const handleError = (error: Error) => {
    toast({
      variant: "destructive",
      title: t("settings.workspaces.saveError"),
      description: resolveApiErrorMessage(error, t),
    });
  };

  const { mutateAsync: createProduct } = useCreateProduct();
  const { mutateAsync: updateProduct } = useUpdateProduct();
  const { mutateAsync: assignProductUsers } = useAssignWorkspaceUsers();
  const { mutateAsync: updateProductUsersMutation } = useUpdateWorkspaceUsers();

  // Função para salvar apenas o produto (sem usuários)
  const { mutate: saveProductOnly, isPending: isSavingProduct } = useMutation({
    mutationFn: async () => {
      if (!formData) {
        throw makeApiError('Form data not filled', { code: 'E_UNEXPECTED_RESPONSE' });
      }

      if (!effectiveOrgId) {
        throw makeApiError('Organization ID not found', { code: 'E_UNEXPECTED_RESPONSE' });
      }


      if (savedWorkspaceId) {
        // Atualizando produto existente
        await updateProduct({
          id: savedWorkspaceId,
          data: {
            name: formData.name,
            // A description é a Soul (AB#86817): só a criação a escreve por aqui; um
            // workspace existente a edita na aba Soul das configurações, com histórico.
            description: workspaceId ? undefined : formData.description,
            type: formData.type,
            organization_id: effectiveOrgId,
            logo: formData.logoFile,
            logo_url: formData.logo_url
          }
        });
        return savedWorkspaceId;
      } else {
        // Criando novo produto
        const creationResult = await createProduct({
          name: formData.name,
          description: formData.description,
          type: formData.type,
          organization_id: effectiveOrgId,
          created_by: user?.id || '',
          logo: formData.logoFile
        });
        
        if (!creationResult?.workspace?.id) {
          throw makeApiError('Failed to create workspace', { code: 'E_UNEXPECTED_RESPONSE' });
        }

        setCreatedWorkspace(creationResult.workspace);
        return creationResult.workspace.id;
      }
    },
    onSuccess: (newWorkspaceId) => {
      setSavedProductId(newWorkspaceId);
      setActiveTab("users");
    },
    onError: handleError,
  });

  // Função para salvar usuários do produto
  const { mutate: saveProductUsers, isPending: isSavingUsers } = useMutation({
    mutationFn: async () => {
      if (!savedWorkspaceId) {
        throw makeApiError('Workspace not saved yet', { code: 'E_UNEXPECTED_RESPONSE' });
      }

      // Se for um produto novo (não estava sendo editado), apenas adicionar usuários
      // O backend já vinculou o criador automaticamente
      if (!workspaceId) {
        // Novo produto: apenas adicionar usuários selecionados (sem remover o criador)
        if (selectedUsers.length > 0) {
          await assignProductUsers({
            workspaceId: savedWorkspaceId,
            userIds: selectedUsers,
          });
        }
      } else {
        // Produto existente: atualizar usuários de forma atômica
        const validUserIds = selectedUsers.filter(
          (id) => id && typeof id === 'string' && id.trim() !== ''
        );

        await updateProductUsersMutation({
          workspaceId: savedWorkspaceId,
          userIds: validUserIds,
        });
      }
      
      return { success: true, workspaceId: savedWorkspaceId };
    },
    onSuccess: async () => {
      const isNewProduct = !workspaceId;
      
      // Disparar evento para o tour saber que um produto foi REALMENTE criado e finalizado
      if (isNewProduct && savedWorkspaceId) {
        window.dispatchEvent(new CustomEvent('workspace-created', { 
          detail: { workspaceId: savedWorkspaceId } 
        }));
      }
      
      await handleSuccess();
    },
    onError: handleError,
  });

  const handleAdvance = () => {
    // Validar e salvar produto antes de avançar
    if (!formData || !formData.name) {
      toast({
        variant: "destructive",
        title: t("common.attention"),
        description: t("settings.workspaces.nameRequired"),
      });
      return;
    }
    saveProductOnly();
  };

  const handleFinish = () => {
    // Salvar apenas os usuários
    saveProductUsers();
  };

  // Função para salvar produto e usuários juntos em modo de edição
  const { mutate: saveAll, isPending: isSavingAll } = useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!formData || !formData.name) {
        throw makeApiError('Workspace name is required', { code: 'E_UNEXPECTED_RESPONSE' });
      }

      if (!effectiveOrgId) {
        throw makeApiError('Organization ID not found', { code: 'E_UNEXPECTED_RESPONSE' });
      }

      if (!savedWorkspaceId) {
        throw makeApiError('Workspace not found', { code: 'E_UNEXPECTED_RESPONSE' });
      }

      if (workspaceId && !hasLoadedInitialUsers) {
        throw new Error(t('products.dialog.usersLoading'));
      }
      
      // Primeiro, atualizar o produto
      await updateProduct({
        id: savedWorkspaceId,
        data: {
          name: formData.name,
          // A description é a Soul (AB#86817), salva pelo painel dela com histórico: aqui
          // ela segue só na criação, senão um texto antigo do formulário sobrescreve a Soul.
          description: workspaceId ? undefined : formData.description,
          type: formData.type,
          organization_id: effectiveOrgId,
          logo: formData.logoFile,
          logo_url: formData.logo_url
        }
      });

      // Depois, atualizar os usuários de forma atômica
      const validUserIds = selectedUsers.filter(
        (id) => id && typeof id === 'string' && id.trim() !== ''
      );

      await updateProductUsersMutation({
        workspaceId: savedWorkspaceId,
        userIds: validUserIds,
      });
    },
    onSuccess: () => handleSuccess(),
    onError: handleError,
  });

  const handleSaveEdit = () => {
    // Validar dados do formulário
    if (!formData || !formData.name) {
      toast({
        variant: "destructive",
        title: t("common.attention"),
        description: t("settings.workspaces.nameRequired"),
      });
      return;
    }
    saveAll();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        {/* Layout principal com altura fixa */}
        <div className="flex flex-col h-[750px]">
          {/* Cabeçalho fixo */}
          <div className="shrink-0 px-6 py-4 border-b">
            <DialogTitle>
              {workspaceId 
                ? t("settings.workspaces.editWorkspace") 
                : t("settings.workspaces.addWorkspace")
              }
            </DialogTitle>
            <DialogDescription>
              {t("settings.workspaces.dialogDescription")}
            </DialogDescription>
          </div>
          
          {/* Área de scroll para o conteúdo das abas */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <Tabs value={activeTab} className="flex flex-col flex-1 min-h-0">
              <div className="px-6 pt-4 pb-2 shrink-0">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger 
                    value="general" 
                    disabled={!workspaceId && activeTab !== "general"}
                    onClick={() => workspaceId && setActiveTab("general")}
                  >
                    {t("settings.workspaces.generalTab")}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="users" 
                    disabled={!workspaceId && activeTab !== "users"}
                    onClick={() => workspaceId && setActiveTab("users")}
                  >
                    {t("settings.workspaces.usersTab")}
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                <TabsContent value="general" className="mt-0 h-full">
                  <WorkspaceForm 
                    workspaceId={workspaceId}
                    onFormDataChange={(data) => {
                      setFormData(data);
                    }}
                    hideSubmitButton={true}
                    initialValues={formData || undefined}
                  />
                </TabsContent>
                
                <TabsContent value="users" className="mt-0 h-full">
                  <WorkspaceUsersForm
                    onSelectedUsersChange={(userIds) => {
                      setSelectedUsers(userIds);
                    }}
                    initialSelectedUsers={memoizedSelectedUsers}
                    currentWorkspaceId={workspaceId}
                    savedWorkspaceId={savedWorkspaceId}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
          
          {/* Rodapé fixo */}
          <div className="shrink-0 border-t bg-accent p-4 flex justify-end">
            {workspaceId ? (
              // Modo de edição: botão único para salvar tudo
              <Button onClick={handleSaveEdit} disabled={isSavingAll || isUsersLoading}>
                {isSavingAll ? t("common.saving") : t("common.save")}
              </Button>
            ) : (
              // Modo de criação: fluxo wizard (sequencial)
              activeTab === "general" ? (
                <Button onClick={handleAdvance} disabled={isSavingProduct}>
                  {isSavingProduct ? t("common.saving") : t("common.actions.next")}
                </Button>
              ) : (
                <Button onClick={handleFinish} disabled={isSavingUsers}>
                  {isSavingUsers ? t("common.saving") : t("common.actions.finish")}
                </Button>
              )
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
